import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { PhoneMissed, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

// Gentle nudge so people always see who tried to reach them, even if they missed the ring.
export function MissedCallBanner() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [missed, setMissed] = useState([]);

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      const items = (data.items || []).filter(
        (n) => n.type === "call" && !n.read && (n.text || "").includes("Missed call")
      );
      setMissed(items);
    } catch { /* noop */ }
  };

  useEffect(() => {
    if (!user) { setMissed([]); return; }
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [user]);

  if (missed.length === 0) return null;

  const latest = missed[0];
  const name = (latest.text.match(/Missed call from (.+?) —/) || [])[1] || "someone";
  const more = missed.length - 1;

  const dismiss = async () => {
    const ids = missed.map((m) => m.notif_id);
    setMissed([]);
    for (const id of ids) { try { await api.post("/notifications/read-one", { notif_id: id }); } catch { /* noop */ } }
  };

  const callBack = async () => {
    await dismiss();
    navigate("/connections");
  };

  return (
    <div className="fixed top-20 inset-x-4 md:left-auto md:right-4 md:w-96 z-[95] pula-card p-4 shadow-lg border border-pula-accent/30 bg-white"
      data-testid="missed-call-banner">
      <div className="flex items-start gap-3">
        <span className="shrink-0 w-9 h-9 rounded-full bg-pula-accent/15 grid place-items-center text-pula-accent">
          <PhoneMissed className="w-4 h-4" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-heading font-bold text-sm text-pula-ink" data-testid="missed-call-name">
            Missed call from {name}
          </p>
          <p className="text-xs text-pula-inksoft font-body mt-0.5">
            {more > 0 ? `and ${more} other missed ${more === 1 ? "call" : "calls"}. ` : ""}They tried to reach you — call them back when you can.
          </p>
          <div className="flex items-center gap-2 mt-3">
            <button onClick={callBack} data-testid="missed-call-callback"
              className="text-xs font-semibold px-3 py-1.5 rounded-full bg-pula-green text-white hover:bg-pula-greenhover transition-colors">
              Call back
            </button>
            <button onClick={dismiss} data-testid="missed-call-dismiss"
              className="text-xs font-medium text-pula-inksoft hover:text-pula-ink transition-colors">Dismiss</button>
          </div>
        </div>
        <button onClick={dismiss} aria-label="Dismiss" className="shrink-0 text-pula-inksoft/60 hover:text-pula-ink">
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
